import type { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { GridMap } from "../generation/GridMap";
import { worldToGrid } from "../rendering/worldCoordinates";

export interface HorizontalPosition {
  readonly x: number;
  readonly z: number;
}

export interface HorizontalMotion {
  x: number;
  z: number;
}

export interface CircleCollisionBody {
  readonly position: HorizontalPosition;
  readonly radius: number;
}

export function isWalkableWorldPosition(grid: GridMap, position: Vector3): boolean {
  return grid.isWalkable(worldToGrid(position.x, position.z, grid), false);
}

export function resolveCircleMotion(
  position: HorizontalPosition,
  motion: HorizontalMotion,
  moverRadius: number,
  blockers: readonly CircleCollisionBody[],
  result: HorizontalMotion = { x: 0, z: 0 },
): HorizontalMotion {
  result.x = motion.x;
  result.z = motion.z;
  for (const blocker of blockers) {
    resolveCircleMotionAgainstBody(
      position,
      result,
      moverRadius,
      blocker.position,
      blocker.radius,
      result,
    );
  }
  return result;
}

export function resolveCircleMotionAgainstBody(
  position: HorizontalPosition,
  motion: HorizontalMotion,
  moverRadius: number,
  blockerPosition: HorizontalPosition,
  blockerRadius: number,
  result: HorizontalMotion = { x: 0, z: 0 },
): HorizontalMotion {
  const minimumDistance = Math.max(0, moverRadius) + Math.max(0, blockerRadius);
  const endX = position.x + motion.x;
  const endZ = position.z + motion.z;
  const endOffsetX = endX - blockerPosition.x;
  const endOffsetZ = endZ - blockerPosition.z;
  const endDistanceSquared = endOffsetX * endOffsetX + endOffsetZ * endOffsetZ;

  if (endDistanceSquared >= minimumDistance * minimumDistance || minimumDistance === 0) {
    result.x = motion.x;
    result.z = motion.z;
    return result;
  }

  let normalX = endOffsetX;
  let normalZ = endOffsetZ;
  let normalLength = Math.sqrt(endDistanceSquared);
  if (normalLength < 0.0001) {
    normalX = position.x - blockerPosition.x;
    normalZ = position.z - blockerPosition.z;
    normalLength = Math.hypot(normalX, normalZ);
  }
  if (normalLength < 0.0001) {
    normalX = motion.x === 0 && motion.z === 0 ? 1 : -motion.x;
    normalZ = motion.x === 0 && motion.z === 0 ? 0 : -motion.z;
    normalLength = Math.hypot(normalX, normalZ);
  }

  result.x = blockerPosition.x + (normalX / normalLength) * minimumDistance - position.x;
  result.z = blockerPosition.z + (normalZ / normalLength) * minimumDistance - position.z;
  return result;
}
